"use client"

import AssetComboIcon from "@src/components/DefuseSDK/components/Asset/AssetComboIcon"
import { buildIntentsExplorerTxUrl } from "@src/components/DefuseSDK/constants/blockchains"
import { formatUsdAmount } from "@src/components/DefuseSDK/utils/format"
import { LIST_TOKENS } from "@src/constants/tokens"
import { HLWithdrawActorProgress } from "@src/features/trade/hyperliquid/withdraw/HLWithdrawProgress"
import { hlWithdrawToIntentsMachine } from "@src/features/trade/hyperliquid/withdraw/hlWithdrawToIntentsMachine"
import { useSelector } from "@xstate/react"
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { type ActorRefFrom, createActor } from "xstate"
import { useActivityDock } from "./ActivityDockProvider"

type HLWithdrawToIntentsActorRef = ActorRefFrom<
  typeof hlWithdrawToIntentsMachine
>

const DOCK_ID = "hl-withdraw-to-intents"
const USDC_ICON = LIST_TOKENS.find(({ symbol }) => symbol === "USDC")?.icon

const HLWithdrawToIntentsContext = createContext<
  HLWithdrawToIntentsActorRef | undefined
>(undefined)

export function HLWithdrawToIntentsProvider({
  children,
}: {
  children: ReactNode
}) {
  const [actorRef] = useState(() =>
    createActor(hlWithdrawToIntentsMachine, {
      input: {
        amount: "0",
        tradingSigner: null,
        intentsUserId: null,
      },
    }).start()
  )
  useHLWithdrawDockItem(actorRef)

  return (
    <HLWithdrawToIntentsContext.Provider value={actorRef}>
      {children}
    </HLWithdrawToIntentsContext.Provider>
  )
}

export function useHLWithdrawToIntentsActor() {
  const actorRef = useContext(HLWithdrawToIntentsContext)

  if (!actorRef) {
    throw new Error(
      "useHLWithdrawToIntentsActor must be used within an HLWithdrawToIntentsProvider"
    )
  }

  return actorRef
}

function useHLWithdrawDockItem(actorRef: HLWithdrawToIntentsActorRef) {
  const { addDockItem, removeDockItem, settleDockItem, updateDockItem } =
    useActivityDock()
  const dockItemActiveRef = useRef(false)
  const state = useSelector(actorRef, (snapshot) => snapshot)

  const amount = Number(state.context.amount) || 0
  const title = `Withdraw ${formatUsdAmount(amount)} USDC from HL`
  const explorerUrl = state.context.depositAddress
    ? buildIntentsExplorerTxUrl(state.context.depositAddress, {
        isConfidential: state.context.isConfidential,
      })
    : undefined
  useEffect(() => {
    if (state.matches("idle")) {
      if (dockItemActiveRef.current) {
        removeDockItem(DOCK_ID)
        dockItemActiveRef.current = false
      }
      return
    }

    const dockItem = {
      title,
      explorerUrl,
      keyValueRows: [],
      renderContent: () => (
        <HLWithdrawActorProgress actorRef={actorRef} size="sm" />
      ),
      isSettled: false,
      settledAt: undefined,
    }

    if (dockItemActiveRef.current) {
      updateDockItem(DOCK_ID, dockItem)
    } else {
      addDockItem({
        id: DOCK_ID,
        icons: [
          <AssetComboIcon key="usdc" icon={USDC_ICON} sizeClassName="size-7" />,
        ],
        ...dockItem,
      })
      dockItemActiveRef.current = true
    }

    if (state.matches("completed") || state.matches("error")) {
      settleDockItem(DOCK_ID)
    }
  }, [
    actorRef,
    addDockItem,
    explorerUrl,
    removeDockItem,
    settleDockItem,
    state,
    title,
    updateDockItem,
  ])
}
